import { useMemo, useState, useEffect } from 'react';
import { NavLink, useSearchParams } from 'react-router-dom';
import { AppSidebar } from '../../components/AppSidebar/AppSidebar';
import { useListCarriers, useDeleteCarrier } from '../../features/carriers/api';
import type { CarrierProfile } from '../../features/carriers/types';
import { CarrierDrawer } from './CarrierDrawer';
import { toast } from 'react-toastify';
import { AuditLogModal } from '../../components/audit/AuditLogModal';
import { Table } from '../../components/Table/Table';
import SearchInput from '../../components/SearchInput/SearchInput';
import { useSortableColumns } from '../../hooks/sorting/useSortableColumns';
import { formatPhone } from '../../utils/formatters';
import './carriers.scss';
import { useDebouncedSearch } from '../../hooks/useDebouncedSearch';

const COI_SOON_DAYS = 30;

// Certificate-of-Insurance status for the carriers table. `tone` drives the pill
// colour; `label` carries the date so the cell is readable at a glance.
function coiStatus(c: CarrierProfile): {
  label: string;
  tone: 'ok' | 'warn' | 'danger' | 'muted';
  title: string;
} {
  if (!c.coiOnFile) {
    return { label: 'None', tone: 'danger', title: 'No Certificate of Insurance on file' };
  }
  const iso = c.coiExpiresAt;
  if (!iso) {
    return { label: 'On file', tone: 'muted', title: 'COI on file — no expiry recorded' };
  }

  const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
  const pretty = `${String(m).padStart(2, '0')}/${String(d).padStart(2, '0')}/${y}`;
  const today = new Date();
  const daysLeft = Math.round(
    (Date.UTC(y, m - 1, d) - Date.UTC(today.getFullYear(), today.getMonth(), today.getDate())) /
      86_400_000
  );

  if (daysLeft < 0) {
    return { label: `Expired ${pretty}`, tone: 'danger', title: `COI expired ${pretty}` };
  }
  if (daysLeft <= COI_SOON_DAYS) {
    return {
      label: `Exp ${pretty}`,
      tone: 'warn',
      title: `COI expires ${pretty} (${daysLeft} day${daysLeft === 1 ? '' : 's'} left)`,
    };
  }
  return { label: `Valid ${pretty}`, tone: 'ok', title: `COI valid until ${pretty}` };
}

// Client-side comparators for each sortable carrier column.
const CARRIER_SORT_VALUES: Record<string, (c: CarrierProfile) => string | number> = {
  company: (c) => c.companyName?.toLowerCase() ?? '',
  phone: (c) => c.phone ?? '',
  dot: (c) => c.dotNumber?.toLowerCase() ?? '',
  mc: (c) => c.mcNumber?.toLowerCase() ?? '',
  orders: (carrier) => carrier.orderCount ?? 0,
  updated: (c) => new Date(c.updatedAt).getTime(),
  // Ascending surfaces the urgent ones first: missing, then soonest to expire.
  coi: (c) => {
    if (!c.coiOnFile) return -1;
    if (c.coiExpiresAt) return new Date(c.coiExpiresAt).getTime();
    return Number.MAX_SAFE_INTEGER;
  },
};

export default function CarriersPage() {
  const [sp, setSp] = useSearchParams();

  const [q, setQ] = useState(sp.get('q') || '');
  const debouncedQ = useDebouncedSearch(q, 350);

  const [page, setPage] = useState(Number(sp.get('page') || 1));
  const [pageSize, setPageSize] = useState(Number(sp.get('pageSize') || 10));

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [auditOpen, setAuditOpen] = useState(false);

  const { sortBy, sortDirection, handleSort } = useSortableColumns({
    defaultSortBy: 'company',
    defaultDirection: 'asc',
    onSortChange: () => setPage(1),
  });

  // Preserve sortBy/sortDir (owned by useSortableColumns) while syncing our own
  // filter/paging state to the URL.
  useEffect(() => {
    setSp(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('q', debouncedQ);
        next.set('page', String(page));
        next.set('pageSize', String(pageSize));
        return next;
      },
      { replace: true }
    );
  }, [debouncedQ, page, pageSize, setSp]);

  // Deep-link: /carriers?carrierId=<id> opens that carrier (e.g. from a load).
  useEffect(() => {
    const id = sp.get('carrierId');
    if (id) {
      setSelectedId(id);
      setSp(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete('carrierId');
          return next;
        },
        { replace: true }
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { data, isLoading, isFetching } = useListCarriers();
  const deleteCarrier = useDeleteCarrier();

  const carriers = useMemo(() => data?.carriers ?? [], [data?.carriers]);

  // Client-side filtering by search query
  const filteredCarriers = useMemo(() => {
    if (!debouncedQ) return carriers;
    const search = debouncedQ.toLowerCase();
    return carriers.filter(
      (c) =>
        c.companyName?.toLowerCase().includes(search) ||
        c.phone?.toLowerCase().includes(search) ||
        c.dotNumber?.toLowerCase().includes(search) ||
        c.mcNumber?.toLowerCase().includes(search) ||
        c.contacts?.some(
          (ct) =>
            ct.name.toLowerCase().includes(search) ||
            ct.email?.toLowerCase().includes(search) ||
            ct.phone?.toLowerCase().includes(search)
        )
    );
  }, [carriers, debouncedQ]);

  // Client-side sorting
  const sortedCarriers = useMemo(() => {
    const getValue = (sortBy && CARRIER_SORT_VALUES[sortBy]) || null;
    if (!getValue) return filteredCarriers;
    const dir = sortDirection === 'asc' ? 1 : -1;
    return [...filteredCarriers].sort((a, b) => {
      const av = getValue(a);
      const bv = getValue(b);
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
  }, [filteredCarriers, sortBy, sortDirection]);

  // Client-side pagination
  const totalCount = sortedCarriers.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const start = (page - 1) * pageSize;
  const end = start + pageSize;
  const paginatedCarriers = sortedCarriers.slice(start, end);

  const handleDelete = async (carrierId: string) => {
    if (!window.confirm('Are you sure you want to delete this carrier?')) return;

    try {
      await deleteCarrier.mutateAsync(carrierId);
      toast.success('Carrier deleted');
    } catch (e: any) {
      toast.error(e?.response?.data?.error || 'Failed to delete carrier');
    }
  };

  const contactNames = (c: CarrierProfile, type: 'DRIVER' | 'DISPATCHER') => {
    const names = (c.contacts ?? []).filter((ct) => ct.type === type).map((ct) => ct.name);
    return names.length > 0 ? names.join(', ') : <span className="muted">—</span>;
  };

  const columns = [
    {
      id: 'company',
      description:
        'Trucking company name. Click to open the carrier. A red dot means its Certificate of Insurance is expired or missing; amber means expiring soon.',
      header: 'Company',
      sortable: true,
      sortKey: 'company',
      render: (c: CarrierProfile) => {
        const status = coiStatus(c);
        const dotTone =
          status.tone === 'danger' ? 'danger' : status.tone === 'warn' ? 'warn' : null;
        return (
          <button
            type="button"
            className="carrier-name-link"
            onClick={() => setSelectedId(c.id)}
            title="Open carrier"
          >
            {dotTone && <span className={`coi-dot coi-dot--${dotTone}`} title={status.title} />}
            {c.companyName}
          </button>
        );
      },
    },
    {
      id: 'phone',
      description: "Main phone number for the carrier's office.",
      header: 'Phone',
      sortable: true,
      sortKey: 'phone',
      render: (c: CarrierProfile) => formatPhone(c.phone),
    },
    {
      id: 'dot',
      description: "US DOT number. The carrier's federal motor carrier registration.",
      header: 'DOT #',
      sortable: true,
      sortKey: 'dot',
      render: (c: CarrierProfile) => c.dotNumber || '—',
    },
    {
      id: 'mc',
      description:
        "Motor Carrier number. The carrier's federal operating authority, printed on the BOL.",
      header: 'MC #',
      sortable: true,
      sortKey: 'mc',
      render: (c: CarrierProfile) => c.mcNumber || '—',
    },
    {
      id: 'coi',
      description:
        'Certificate of Insurance status, from the carrier’s uploaded documents. Sort to surface missing or expiring certs.',
      header: 'COI',
      sortable: true,
      sortKey: 'coi',
      render: (c: CarrierProfile) => {
        const status = coiStatus(c);
        return (
          <span className={`coi-pill coi-pill--${status.tone}`} title={status.title}>
            {status.label}
          </span>
        );
      },
    },
    {
      id: 'drivers',
      description:
        "Driver contacts on file for this carrier. Drivers don't see the carrier rate on a BOL.",
      header: 'Drivers',
      render: (c: CarrierProfile) => contactNames(c, 'DRIVER'),
    },
    {
      id: 'dispatchers',
      description: 'Dispatcher contacts on file. Dispatchers do see the carrier rate on a BOL.',
      header: 'Dispatchers',
      render: (c: CarrierProfile) => contactNames(c, 'DISPATCHER'),
    },
    {
      id: 'orders',
      description:
        'Number of distinct orders assigned to this carrier. Click the number to view those orders.',
      header: 'Orders',
      sortable: true,
      sortKey: 'orders',
      defaultWidth: 100,
      minWidth: 80,
      maxWidth: 140,
      render: (carrier: CarrierProfile) => {
        const orderCount = carrier.orderCount ?? 0;

        if (orderCount === 0) {
          return <span className="muted">0</span>;
        }

        const searchParams = new URLSearchParams();

        searchParams.append('carrier', carrier.id);

        return (
          <NavLink
            to={`/orders?${searchParams.toString()}`}
            className="carrier-order-count"
            title={`View orders assigned to ${carrier.companyName}`}
            aria-label={`View ${orderCount} order${
              orderCount === 1 ? '' : 's'
            } assigned to ${carrier.companyName}`}
            onClick={(event) => {
              event.stopPropagation();
            }}
          >
            {orderCount}
          </NavLink>
        );
      },
    },
    {
      id: 'updated',
      description: 'When this carrier record was last changed.',
      header: 'Updated',
      sortable: true,
      sortKey: 'updated',
      render: (c: CarrierProfile) => new Date(c.updatedAt).toLocaleString(),
    },
    {
      id: 'actions',
      description: 'Edit or remove this carrier.',
      header: 'Actions',
      className: 'col-actions ta-right',
      render: (c: CarrierProfile) => (
        <div className="actions-inner" style={{ gap: 8, justifyContent: 'flex-end' }}>
          <button className="btn btn--ghost" onClick={() => setSelectedId(c.id)}>
            Edit
          </button>
          <button className="btn btn--ghost btn--danger" onClick={() => handleDelete(c.id)}>
            Delete
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="layout">
      <AppSidebar />
      <div className="card">
        {/* Header */}
        <div className="flex-between" style={{ marginBottom: 12 }}>
          <div className="carrier_title">
            <h2 className="card__title">Carriers</h2>
            <p className="card__subtitle">
              Showing {paginatedCarriers.length} of {totalCount} total
            </p>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn--primary" onClick={() => setShowCreate(true)}>
              Add Carrier
            </button>
            <button className="btn btn--primary" onClick={() => setAuditOpen(true)}>
              Audit Log
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="card card--bleed-top carriers-filters">
          <div className="grid carriers-filters__grid">
            <div className="form-control">
              <label>Search</label>
              <SearchInput
                placeholder="Company name, phone, DOT/MC, email…"
                value={q}
                onValueChange={(value) => {
                  setQ(value);
                  setPage(1);
                }}
              />
            </div>

            <div className="form-control">
              <label>Page size</label>
              <select
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setPage(1);
                }}
              >
                {[10, 20, 50].map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Table */}
        <Table
          columns={columns}
          data={paginatedCarriers}
          isLoading={isLoading}
          emptyMessage={
            debouncedQ
              ? 'No carriers match your search.'
              : 'No carriers yet. Click "Add Carrier" to get started.'
          }
          className="carriers-table"
          sortBy={sortBy}
          sortDirection={sortDirection}
          onSort={handleSort}
        />

        {/* Drawer */}
        {(selectedId || showCreate) && (
          <CarrierDrawer
            carrierId={selectedId}
            onClose={() => {
              setSelectedId(null);
              setShowCreate(false);
            }}
          />
        )}

        {/* Pagination */}
        <div className="actions" style={{ justifyContent: 'space-between' }}>
          <div />
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button
              className="btn btn--ghost"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
            >
              Prev
            </button>
            <div style={{ minWidth: 90, textAlign: 'center' }}>
              Page {page} of {totalPages}
            </div>
            <button
              className="btn btn--ghost"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
            >
              Next
            </button>
          </div>
        </div>

        {isFetching && (
          <div className="muted" style={{ marginTop: 8 }}>
            Refreshing…
          </div>
        )}
        <AuditLogModal open={auditOpen} onClose={() => setAuditOpen(false)} entityType="CARRIER" />
      </div>
    </div>
  );
}
